import {CircleHelp} from "lucide-react";
import {useState} from "react";
import type {ComponentProps} from "react";

import {Button} from "@/components/ui/button";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {useStoredMe} from "@/src/modules/core/store/me";

import {
  getHelpTourPlacementConfig,
  resolveHelpTourRunOptions,
  type HelpTourPlacement,
} from "../lib/help-tour-placement";
import {runHelpTour} from "../lib/run-help-tour";

type HelpTourTriggerProps = {
  tourId: string;
  /**
   * Standard DAR look & feel: `list` = ghost icon in toolbar; `sheet` = outline "Show me how" in header.
   * Explicit size/variant/className/restoreScroll override placement defaults.
   */
  placement?: HelpTourPlacement;
  label?: string;
  className?: string;
  size?: ComponentProps<typeof Button>["size"];
  variant?: ComponentProps<typeof Button>["variant"];
  /** Off for full-viewport sheets — banner tints iOS status bar; popover progress is enough. */
  showBanner?: boolean;
  restoreScroll?: boolean;
};

export function HelpTourTrigger({
  tourId,
  placement,
  label: labelProp,
  className,
  size: sizeProp,
  variant: variantProp,
  showBanner: showBannerProp,
  restoreScroll: restoreScrollProp,
}: HelpTourTriggerProps) {
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const [isStarting, setIsStarting] = useState(false);

  const placementConfig = placement ? getHelpTourPlacementConfig(placement) : null;
  const label = labelProp ?? placementConfig?.label ?? "Show me how";
  const size = sizeProp ?? placementConfig?.size ?? "sm";
  const variant = variantProp ?? placementConfig?.variant ?? "outline";
  const classNameResolved = cn(placementConfig?.className, className);
  const runOptions = resolveHelpTourRunOptions(placement, {
    showBanner: showBannerProp,
    restoreScroll: restoreScrollProp,
  });

  const handleClick = async () => {
    if (viewingAsUser) {
      return;
    }

    setIsStarting(true);
    try {
      await runHelpTour(tourId, runOptions);
    } finally {
      setIsStarting(false);
    }
  };

  if (viewingAsUser) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant={variant}
            size={size}
            className={classNameResolved}
            disabled={isStarting}
            aria-label={size === "icon" ? label : undefined}
            title={label}
            onClick={() => void handleClick()}
          >
            <CircleHelp className="size-4 shrink-0" />
            {size !== "icon" ? <span>{label}</span> : <span className="sr-only">{label}</span>}
          </Button>
        }
      />
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
